import { useState, useEffect, useCallback, useLayoutEffect, useRef, useMemo } from 'react'
import { Box, Paper, IconButton, TextField, Typography, Fab, InputAdornment, Menu, MenuItem, Alert } from '@mui/material'
import { Close, MoreVert, ArrowUpward, Mic, Stop, Cancel, ChatBubble } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useApolloClient } from '@apollo/react-hooks'
import { useSelector } from 'react-redux'
import { useNavigateWithQueryParams, useWindowDimensions } from '../Utilities'
import { useKeycloak } from '../Keycloak'
import { useGetAccessMonitoringQuery } from '../../api/graphqlApi'
import { getData } from '../../api/dataSlice'
import { isMobile, useMobileOrientation } from 'react-device-detect'
import * as ChatManager from './ChatManager'

const SPEECH_LANGS = { de: 'de-DE', en: 'en-US', nl: 'nl-NL' }
const EDGE_MARGIN = 8 // breathing room from every viewport edge
const ANCHOR_MARGIN = isMobile ? 4 : 18 // offset from the docked corner
const FAB_DRAG_THRESHOLD_PX = 6 // pointer must travel this far before a Fab press becomes a drag
const CHAT_CLOSE_MS = 250

// corner ∈ 'BR' | 'BL' | 'TR' | 'TL'; top corners offset by appbarHeight so the widget never overlaps the header
const getAnchorStyle = (corner, appbarHeight) => {
    switch (corner) {
        case 'BL': return { bottom: ANCHOR_MARGIN, left: ANCHOR_MARGIN }
        case 'TR': return { top: appbarHeight + ANCHOR_MARGIN, right: ANCHOR_MARGIN }
        case 'TL': return { top: appbarHeight + ANCHOR_MARGIN, left: ANCHOR_MARGIN }
        default:   return { bottom: ANCHOR_MARGIN, right: ANCHOR_MARGIN } // 'BR'
    }
}

// nearest viewport corner of a DOMRect — snap target for Fab release and dialog close
const nearestCorner = (rect) => {
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2
    return (cy < window.innerHeight / 2 ? 'T' : 'B') + (cx < window.innerWidth / 2 ? 'L' : 'R')
}

// (dx, dy) bounds that keep a corner-anchored element of (width × height) inside the safe viewport
const getDragBounds = (corner, width, height, appbarHeight) => {
    const anchoredRight = corner === 'BR' || corner === 'TR'
    const anchoredBottom = corner === 'BR' || corner === 'BL'
    let minDx = anchoredRight ? EDGE_MARGIN - window.innerWidth + ANCHOR_MARGIN + width : EDGE_MARGIN - ANCHOR_MARGIN
    let maxDx = anchoredRight ? ANCHOR_MARGIN - EDGE_MARGIN : window.innerWidth - ANCHOR_MARGIN - width - EDGE_MARGIN
    let minDy = anchoredBottom ? appbarHeight + EDGE_MARGIN - window.innerHeight + ANCHOR_MARGIN + height : EDGE_MARGIN - ANCHOR_MARGIN
    let maxDy = anchoredBottom ? ANCHOR_MARGIN - EDGE_MARGIN : window.innerHeight - EDGE_MARGIN - appbarHeight - ANCHOR_MARGIN - height
    // when the element already fills the safe area, lock that axis at the anchor (no room to drag — otherwise clamp inverts and snaps to the wrong edge)
    if (minDx > maxDx) minDx = maxDx = 0
    if (minDy > maxDy) minDy = maxDy = 0
    return { minDx, maxDx, minDy, maxDy }
}

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi)

export default function ChatWidget() {

    const { t, i18n } = useTranslation()
    const { getUsername, hasRouteAccess } = useKeycloak()
    const { appbarHeight } = useWindowDimensions()
    const { isPortrait } = useMobileOrientation()

    const navigate = useNavigateWithQueryParams()
    const conf = window.conf || {}
    const username = getUsername()

    // Dashboard + Diary + Report: published by their accordions on mount — read from Redux feature flags
    const { hasDashboard, hasDiary, hasReport } = useSelector(getData).featureFlags
    
    // Access: role + data exists + authorized
    const { showAccess } = useGetAccessMonitoringQuery(undefined, {
        selectFromResult: ({ data, isSuccess }) => {
            if (!isSuccess) return { showAccess: false }
            const hasAnyData = (conf.CAMERAS ?? []).some(elem => (data?.byCameraId?.[elem?.id]?.length || 0) > 0)
            const hasAccessPage = hasRouteAccess(conf.ROUTES?.ACCESS)
            return { showAccess: hasAnyData && hasAccessPage }
        }
    })

    // Build capabilities object from detected flags
    const capabilities = useMemo(() => ({
        pages: {
            home: true,
            live: true,
            timelapse: true,
            archive: true,
            mysite: true,
            access: !!showAccess,
        },
        mysite: {
            dashboard: !!hasDashboard,
            report: !!hasReport,
            diary: !!hasDiary,
            download: !isMobile,
        },
        features: {
            livestream: (conf.CAMERAS || []).some(c => !!c.video),
        },
    // conf and isMobile are stable — only the detected-feature flags need to be in deps
    }), [hasDashboard, hasDiary, showAccess, hasReport]) // eslint-disable-line react-hooks/exhaustive-deps

    const [isOpen, setIsOpen] = useState(false)
    const [isClosing, setIsClosing] = useState(false) // keeps Paper mounted during the exit animation
    const [messages, setMessages] = useState([{ role: 'assistant', text: t('chatbot.greeting') }])
    const [input, setInput] = useState('')
    const [loading, setLoading] = useState(false)
    const [isListening, setIsListening] = useState(false)
    const [speechSupported, setSpeechSupported] = useState(false)
    const [micDenied, setMicDenied] = useState(false) // surfaced as an inline Alert above the input row
    const [corner, setCorner] = useState(isMobile ? 'BL' :'BR') // shared dock corner; updated by Fab snap and by dialog close
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 }) // dialog translate from corner anchor; reset on close (corner takes over)
    const [fabDragOffset, setFabDragOffset] = useState({ x: 0, y: 0 }) // Fab translate while dragging; reset on release (corner takes over)
    const [closingPosition, setClosingPosition] = useState(null) // pre-close snapshot so the dialog slides out in place while corner already points at the new Fab corner
    const [isDragging, setIsDragging] = useState(false) // drives the grab/grabbing cursor
    const [menuAnchor, setMenuAnchor] = useState(null) // header MoreVert menu anchor; null = closed

    const recognitionRef = useRef(null)
    const keepListeningRef = useRef(false) // true while user is recording — drives auto-restart in onend
    const finalTranscriptRef = useRef('') // final results carried across browser-side auto-restarts
    const messagesEndRef = useRef(null)
    const widgetRef = useRef(null)
    const wasOpenRef = useRef(false) // tracks previous isOpen so the auto-scroll effect can pick instant vs smooth
    const closeTimerRef = useRef(null)
    const initDone = useRef(false)
    const dragStateRef = useRef(null) // { startX, startY, startOffsetX, startOffsetY } during a header drag
    const fabDragStateRef = useRef(null) // { startX, startY, moved } during a Fab drag
    const fabSuppressClickRef = useRef(false) // swallow the click event that fires after a snap pointerup
    const greetingKeyRef = useRef('chatbot.greeting') // which greeting to re-translate when language changes (flipped by resetChat)

    // snap corner to where the dialog sits, then play the slide-down; closingPosition keeps the dialog in place while the Fab fades in at the new corner
    const requestClose = useCallback(() => {
        if (closeTimerRef.current) return // already closing
        const dragged = dragOffset.x !== 0 || dragOffset.y !== 0 // only re-snap the corner when the user actually moved the dialog (avoids ambiguity on mobile, where the dialog spans the full viewport and nearestCorner can't tell L from R)
        const newCorner = dragged && widgetRef.current
            ? nearestCorner(widgetRef.current.getBoundingClientRect())
            : corner
        setClosingPosition({ corner, dragOffset })
        setCorner(newCorner)
        setDragOffset({ x: 0, y: 0 })
        setIsClosing(true)
        closeTimerRef.current = setTimeout(() => {
            setIsOpen(false)
            setIsClosing(false)
            setClosingPosition(null)
            closeTimerRef.current = null
        }, CHAT_CLOSE_MS)
    }, [corner, dragOffset])

    // register navigate function so ChatManager can navigate from anywhere
    useEffect(() => {
        ChatManager.setNavigate(navigate)
        return () => ChatManager.setNavigate(null)
    }, [navigate])

    // cleanup pending close timer on unmount
    useEffect(() => () => {
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    }, [])

    // close on mobile orientation change — dialog size and drag offset are tuned to the current viewport shape and don't translate cleanly into the other
    useEffect(() => {
        if (!isMobile || !isOpen || isClosing) return
        requestClose()
    }, [isPortrait]) // eslint-disable-line react-hooks/exhaustive-deps

    // init chatbot system once on first open
    useEffect(() => {
        if (isOpen && !initDone.current) {
            initDone.current = true
            ChatManager.init({ username, capabilities, language: i18n.language })
        }
    }, [isOpen, username, capabilities, i18n.language])

    // register Apollo client so per-turn subscription can subscribe imperatively from outside React (mirrors the setNavigate / setActivateDashboard setter pattern)
    const apolloClient = useApolloClient()
    useEffect(() => {
        ChatManager.setApolloClient(apolloClient)
        return () => ChatManager.setApolloClient(null)
    }, [apolloClient])

    // Escape: first press cancels an in-flight turn (mirrors the red Cancel button), next press closes the widget
    useEffect(() => {
        if (!isOpen || isClosing) return
        const onKeyDown = (e) => {
            if (e.key !== 'Escape') return
            if (loading) ChatManager.cancelCurrentTurn()
            else requestClose()
        }
        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [isOpen, isClosing, loading, requestClose])

    // re-translate the greeting on language switch while the conversation is pristine
    useEffect(() => {
        setMessages(prev => {
            if (prev.length !== 1 || prev[0].role !== 'assistant') return prev
            const text = t(greetingKeyRef.current)
            return prev[0].text === text ? prev : [{ role: 'assistant', text }]
        })
    }, [i18n.language]) // eslint-disable-line react-hooks/exhaustive-deps

    // auto-scroll to bottom — instant on (re)open so the user lands at the latest message without seeing a top→bottom flicker, smooth for in-session updates. useLayoutEffect runs before paint so 'auto' scroll lands before the user sees anything.
    useLayoutEffect(() => {
        if (!messagesEndRef.current) return
        const justOpened = isOpen && !wasOpenRef.current
        wasOpenRef.current = isOpen
        messagesEndRef.current.scrollIntoView({ behavior: justOpened ? 'auto' : 'smooth' })
    }, [messages, loading, isOpen])

    // speech recognition setup — continuous mode + auto-restart so a brief pause (or Chrome's ~60s session cap) doesn't kill recording
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
        if (!SpeechRecognition) return
        setSpeechSupported(true)
        const recognition = new SpeechRecognition()
        recognition.lang = SPEECH_LANGS[i18n.language] || 'de-DE'
        recognition.interimResults = true
        recognition.continuous = true // don't auto-stop on first pause — restart on onend if user still wants to record
        recognition.onstart = () => setIsListening(true) // browser confirmed mic is live — flip UI now so a denied/failed start doesn't leave us stuck in "listening"
        recognition.onresult = (event) => {
            // only walk new results since resultIndex; older ones are already folded into finalTranscriptRef (also covers post-restart sessions where event.results resets)
            let interim = ''
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const r = event.results[i]
                if (r.isFinal) finalTranscriptRef.current += r[0].transcript
                else interim += r[0].transcript
            }
            setInput(finalTranscriptRef.current + interim)
        }
        recognition.onend = () => {
            // Chrome silently ends the session every ~60s even with continuous=true — restart while the user is still recording
            if (keepListeningRef.current) {
                try { recognition.start() }
                catch (e) {
                    console.error('[ChatWidget] recognition.start() on auto-restart failed:', e)
                    keepListeningRef.current = false
                    setIsListening(false)
                }
            } else {
                setIsListening(false)
            }
        }
        recognition.onerror = (event) => {
            if (event.error === 'no-speech' || event.error === 'aborted') return // benign — onend fires next and handles restart vs. stop
            console.error('[ChatWidget] SpeechRecognition error:', event.error)
            keepListeningRef.current = false // permission/hardware error — stop the restart loop
            setIsListening(false)
            if (event.error === 'not-allowed') setMicDenied(true)
        }
        recognitionRef.current = recognition
        return () => {
            keepListeningRef.current = false // prevent onend from restarting a torn-down recognizer
            recognition.abort()
        }
    // t is stable — only language switches should rebuild the recognizer
    }, [i18n.language]) // eslint-disable-line react-hooks/exhaustive-deps

    // stop recording when the widget closes so the mic isn't held open in the background (the recognizer outlives the Paper), and drop any stale mic-denied alert so it doesn't reappear on next open
    useEffect(() => {
        if (isOpen) return
        setMicDenied(false)
        if (!isListening) return
        keepListeningRef.current = false
        recognitionRef.current?.abort()
        setIsListening(false)
    }, [isOpen, isListening])

    // wait for dashboardActions to become available after navigation (polls every 200ms, max 15s)
    const waitForDashboardActions = useCallback(() => {
        return new Promise(resolve => {
            let elapsed = 0
            const id = setInterval(() => {
                const actions = ChatManager.getDashboardActions()
                if (actions) { clearInterval(id); resolve(actions) }
                elapsed += 200
                if (elapsed >= 15000) { clearInterval(id); resolve(null) }
            }, 200)
        })
    }, [])

    // execute dashboard actions, returns description strings
    const executeDashboardActions = useCallback((actions, dashboardActions) => {
        const descriptions = []
        const typeFilterAct = actions.find(a => a.action === 'show_only_detections')
        const allTypesAct = actions.find(a => a.action === 'show_all_detections')
        const setDatesAct = actions.find(a => a.action === 'set_dates')
        const typesOverride = setDatesAct && typeFilterAct ? typeFilterAct.types
            : setDatesAct && allTypesAct ? null : undefined

        for (const act of actions) {
            switch (act.action) {
                case 'show_only_detections':
                    if (!setDatesAct) dashboardActions.showOnlyDetections(act.types)
                    descriptions.push(t('chatbot.act_only') + ' ' + act.types.join(', '))
                    break
                case 'show_all_detections':
                    if (!setDatesAct) dashboardActions.showAllDetections()
                    descriptions.push(t('chatbot.act_all_types'))
                    break
                case 'set_dates':
                    dashboardActions.setDates(act.start, act.end, typesOverride)
                    descriptions.push(t('chatbot.act_dates'))
                    break
                case 'switch_tab': {
                    // map German tab names to internal values (defense against unmapped actions)
                    const tabMap = { erkennungen: 'detections', wetter: 'weather' }
                    const tab = tabMap[act.tab] || act.tab
                    dashboardActions.switchTab(tab)
                    descriptions.push(t('chatbot.act_tab') + ' ' + tab)
                    break
                }
                case 'show_weather_streams':
                    dashboardActions.showWeatherStreams(act.streams)
                    descriptions.push(t('chatbot.act_streams') + ' ' + act.streams.join(', '))
                    break
                case 'reset_view':
                    dashboardActions.resetView()
                    descriptions.push(t('chatbot.act_reset'))
                    break
                case 'load_data':
                    descriptions.push(t('chatbot.act_loading'))
                    break
                case 'open_viewer': {
                    // backend may put combined date+time into act.date (either "DD.MM.YYYY HH:mm" or "YYYY-MM-DD HH:mm") — split into a separate date and ISO timestamp
                    let viewerDate = act.date || ''
                    let viewerTimestamp = act.timestamp || null
                    if (viewerDate.includes(' ') && !viewerTimestamp) {
                        const [datePart, timePart] = viewerDate.split(' ', 2)
                        const dotParts = datePart.split('.')
                        if (dotParts.length === 3) { // German: DD.MM.YYYY → ISO YYYY-MM-DD
                            let y = dotParts[2]; if (y.length === 2) y = '20' + y
                            viewerTimestamp = `${y}-${dotParts[1]}-${dotParts[0]} ${timePart}`
                        } else if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) { // already ISO
                            viewerTimestamp = `${datePart} ${timePart}`
                        }
                        viewerDate = datePart
                    }
                    dashboardActions.openViewer(viewerDate, viewerTimestamp, act.types || null)
                    descriptions.push(t('chatbot.act_viewer') + (viewerDate ? ' ' + t('chatbot.act_for') + ' ' + viewerDate : ''))
                    break
                }
                default:
                    break
            }
        }
        return descriptions
    }, [t])

    // send message to AI
    const handleSend = useCallback(async () => {
        const text = input.trim()
        if (!text || loading) return

        setInput('')
        setMessages(prev => [...prev, { role: 'user', text }])
        setLoading(true)

        try {
            const json = await ChatManager.sendMessage(text, {
                language: i18n.language,
            })

            // system message if user clicked Cancel mid-flight or no terminal event arrived within TURN_TIMEOUT_MS (subscription drop, server restart)
            if (json.debug?.cancelled) {
                setMessages(prev => [...prev, { role: 'system', text: t('chatbot.cancelled') }])
                return
            }
            if (json.debug?.timedOut) {
                setMessages(prev => [...prev, { role: 'system', text: t('chatbot.no_response_retry') }])
                return
            }

            const newMsgs = [{ role: 'assistant', text: json.reply || t('chatbot.no_response') }]

            if (json.actions?.length) {
                let dashboardActions = ChatManager.getDashboardActions()

                // if dashboard is not active, navigate there and wait for it
                if (!dashboardActions) {
                    setMessages(prev => [...prev, ...newMsgs, { role: 'system', text: t('chatbot.act_navigating') }])
                    newMsgs.length = 0 // prevent re-adding reply in final setMessages
                    await ChatManager.navigateToDashboard()
                    dashboardActions = await waitForDashboardActions()
                }

                if (dashboardActions) {
                    const descriptions = executeDashboardActions(json.actions, dashboardActions)
                    if (descriptions.length) newMsgs.push({ role: 'system', text: t('chatbot.updated') + ' ' + descriptions.join('; ') })
                }
            }

            setMessages(prev => [...prev, ...newMsgs])
        } catch (e) {
            setMessages(prev => [...prev, { role: 'system', text: t('chatbot.error') }])
        } finally {
            setLoading(false)
        }
    // setters (setInput/setMessages/...) are stable — not in deps on purpose
    }, [input, loading, waitForDashboardActions, executeDashboardActions, t, i18n.language]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }

    const toggleListening = useCallback(() => {
        const recognition = recognitionRef.current
        if (!recognition) return
        if (isListening) {
            keepListeningRef.current = false // user-initiated stop — don't restart in onend
            recognition.stop() // stop (not abort) so the trailing audio still becomes a final result
        } else {
            setInput('')
            finalTranscriptRef.current = ''
            keepListeningRef.current = true
            setMicDenied(false) // clear any stale denial alert from a previous attempt
            try { recognition.start(); setIsListening(true) }
            catch (e) {
                console.error('[ChatWidget] recognition.start() failed:', e)
                keepListeningRef.current = false
                setIsListening(false)
            }
        }
    }, [isListening])

    const resetChat = () => {
        ChatManager.resetConversation()
        greetingKeyRef.current = 'chatbot.new_greeting' // language-change effect now re-translates the post-reset greeting
        setMessages([{ role: 'assistant', text: t('chatbot.new_greeting') }])
    }

    // header bar acts as the drag handle; position:fixed + translate(x,y) so we shift relative to the bottom-right anchor without re-flowing the layout
    const onHeaderPointerDown = useCallback((e) => {
        if (e.button !== undefined && e.button !== 0) return // left mouse button / touch / pen only
        if (e.target.closest('button')) return // let Refresh / Close icons keep their clicks
        dragStateRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            startOffsetX: dragOffset.x,
            startOffsetY: dragOffset.y,
        }
        setIsDragging(true)
        e.currentTarget.setPointerCapture(e.pointerId)
    }, [dragOffset.x, dragOffset.y])

    // clamp drag inside the safe viewport (below AppBar, EDGE_MARGIN from edges), per anchor corner
    const onHeaderPointerMove = useCallback((e) => {
        const drag = dragStateRef.current
        if (!drag) return
        const paper = widgetRef.current
        if (!paper) return
        const rect = paper.getBoundingClientRect()
        const { minDx, maxDx, minDy, maxDy } = getDragBounds(corner, rect.width, rect.height, appbarHeight)
        setDragOffset({
            x: clamp(drag.startOffsetX + (e.clientX - drag.startX), minDx, maxDx),
            y: clamp(drag.startOffsetY + (e.clientY - drag.startY), minDy, maxDy),
        })
    }, [corner, appbarHeight])

    const onHeaderPointerUp = useCallback((e) => {
        if (!dragStateRef.current) return
        dragStateRef.current = null
        setIsDragging(false)
        if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId)
        }
    }, [])

    // Fab drag: threshold separates drag from click; release snaps to the nearest corner and swallows the trailing click
    const onFabPointerDown = useCallback((e) => {
        if (e.button !== undefined && e.button !== 0) return
        fabDragStateRef.current = { startX: e.clientX, startY: e.clientY, moved: false }
        e.currentTarget.setPointerCapture(e.pointerId)
    }, [])

    const onFabPointerMove = useCallback((e) => {
        const drag = fabDragStateRef.current
        if (!drag) return
        const dx = e.clientX - drag.startX
        const dy = e.clientY - drag.startY
        if (!drag.moved && Math.hypot(dx, dy) < FAB_DRAG_THRESHOLD_PX) return
        drag.moved = true
        setFabDragOffset({ x: dx, y: dy })
    }, [])

    const onFabPointerUp = useCallback((e) => {
        const drag = fabDragStateRef.current
        if (!drag) return
        fabDragStateRef.current = null
        if (e.currentTarget.hasPointerCapture?.(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
        if (!drag.moved) return // tap/click — let onClick handle opening
        const newCorner = nearestCorner(e.currentTarget.getBoundingClientRect())
        fabSuppressClickRef.current = true
        setCorner(newCorner)
        setFabDragOffset({ x: 0, y: 0 })
    }, [])

    const onFabClick = useCallback(() => {
        if (fabSuppressClickRef.current) { fabSuppressClickRef.current = false; return }
        if (isClosing) return
        setIsOpen(true)
    }, [isClosing])

    // on mobile, top-anchored (TR/TL) or dragged-up (BR/BL with negative y) dialogs get pushed off-screen by the soft keyboard — snap to the matching bottom corner so the input floats above it
    const onInputFocus = useCallback(() => {
        if (!isMobile) return
        const isTopAnchored = corner === 'TR' || corner === 'TL'
        const isDraggedUp = (corner === 'BR' || corner === 'BL') && dragOffset.y < 0
        if (!isTopAnchored && !isDraggedUp) return
        if (corner === 'TR') setCorner('BR')
        else if (corner === 'TL') setCorner('BL')
        setDragOffset(prev => ({ x: prev.x, y: 0 }))
    }, [corner, dragOffset.y])

    // render both Fab and Paper during the close transition so they cross-fade (Paper slides down + fades out, Fab fades+scales in at the same corner); at rest only one of the two is mounted
    return (
        <>
            {(!isOpen || isClosing) && (
                <Fab
                    onClick={onFabClick}
                    onPointerDown={onFabPointerDown}
                    onPointerMove={onFabPointerMove}
                    onPointerUp={onFabPointerUp}
                    onPointerCancel={onFabPointerUp}
                    title={t('chatbot.title')}
                    color="primary"
                    sx={{
                        position: 'fixed',
                        ...getAnchorStyle(corner, appbarHeight),
                        zIndex: 1400,
                        animation: isClosing ? 'chatFabFadeIn 0.25s ease-out' : 'none',
                        pointerEvents: isClosing ? 'none' : 'auto',
                        transform: isClosing ? undefined : `translate(${fabDragOffset.x}px, ${fabDragOffset.y}px)`, // skip static translate during close — chatFabFadeIn's keyframes own transform
                        touchAction: 'none',
                    }}
                    size={isMobile ? 'small' : 'medium'}
                >
                    <ChatBubble />
                </Fab>
            )}
            {(isOpen || isClosing) && (
                // outer wrapper owns corner anchor + drag translate; inner Paper owns the slide animation; closingPosition keeps it in place during close
                <Box
                    sx={{
                        position: 'fixed',
                        ...getAnchorStyle(closingPosition?.corner ?? corner, appbarHeight),
                        zIndex: 1401,
                        transform: `translate(${(closingPosition?.dragOffset ?? dragOffset).x}px, ${(closingPosition?.dragOffset ?? dragOffset).y}px)`,
                    }}
                >
                    <Paper
                        ref={widgetRef}
                        elevation={8}
                        sx={{
                            width: isMobile && isPortrait ? '90vw' : 380, maxWidth: 380,
                            height: isMobile ? (isPortrait ? '50vh' : 'calc(100vh - 80px)') : 320, maxHeight: 320,
                            display: 'flex', flexDirection: 'column',
                            borderRadius: 1, overflow: 'hidden', bgcolor: "unset",
                            animation: isClosing ? 'chatSlideDown 0.25s ease-in forwards' : 'chatSlideUp 0.25s ease-out',
                        }}
                    >

                        {/* header — drag handle on desktop */}

                        <Box
                            onPointerDown={onHeaderPointerDown}
                            onPointerMove={onHeaderPointerMove}
                            onPointerUp={onHeaderPointerUp}
                            onPointerCancel={onHeaderPointerUp}
                            sx={{
                                bgcolor: '#003366', color: 'white', px: 2,
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                minHeight: 48,
                                cursor: isDragging ? 'grabbing' : 'grab',
                                userSelect: 'none',
                                touchAction: 'none',
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 14, pl: 1 }}>
                                {t('chatbot.title')}
                            </Typography>
                            <Box>
                                <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} title={t('chatbot.menu')} sx={{ color: 'white' }}>
                                    <MoreVert fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={requestClose} title={t('chatbot.close')} sx={{ color: 'white' }}>
                                    <Close fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>

                        {/* menu rendered as a sibling of the header — keeps its synthetic pointer events from bubbling into onHeaderPointerDown (which would call setPointerCapture and steal MenuItem clicks) */}
                        
                        <Menu
                            anchorEl={menuAnchor}
                            open={Boolean(menuAnchor)}
                            onClose={() => setMenuAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            sx={{
                                zIndex: 1500, // zIndex must beat the chat Paper (1401) so the menu doesn't hide behind the widget
                                '& .MuiPaper-root': { minWidth: 180, mt: 0.5 },
                                '& .MuiList-root': { py: 0.5 },
                                '& .MuiMenuItem-root': { fontSize: 13, minHeight: 32, py: 0.75 },
                            }}
                        >
                            <MenuItem onClick={() => { resetChat(); setMenuAnchor(null) }}>
                                {t('chatbot.new')}
                            </MenuItem>
                            <MenuItem
                                onClick={() => { setCorner(isMobile ? 'BL' : 'BR'); setDragOffset({ x: 0, y: 0 }); setMenuAnchor(null) }}
                                disabled={corner === 'BR' && dragOffset.x === 0 && dragOffset.y === 0}
                            >
                                {t('chatbot.reset_position')}
                            </MenuItem>
                        </Menu>

                        {/* messages */}

                        <Box sx={{
                            flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1,
                            minHeight: 0,
                            bgcolor: '#fafafa',
                        }}>
                            {messages.map((msg, i) => (
                                <Box key={i} sx={{
                                    alignSelf: msg.role === 'user' ? 'flex-end' : msg.role === 'system' ? 'center' : 'flex-start',
                                    bgcolor: msg.role === 'user' ? '#003366' : msg.role === 'system' ? 'transparent' : '#f0f2f5',
                                    color: msg.role === 'user' ? 'white' : msg.role === 'system' ? '#999' : '#2c3e50',
                                    fontStyle: msg.role === 'system' ? 'italic' : 'normal',
                                    fontSize: msg.role === 'system' ? 12 : 13,
                                    px: msg.role === 'system' ? 1 : 1.5,
                                    py: msg.role === 'system' ? 0.5 : 1,
                                    borderRadius: 1,
                                    maxWidth: msg.role === 'system' ? '100%' : '85%',
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.5,
                                }}>
                                    {msg.text}
                                </Box>
                            ))}

                            {/* typing indicator */}

                            {loading && (
                                <Box sx={{ alignSelf: 'flex-start', display: 'flex', gap: 0.5, p: 1 }}>
                                    <Box component="span" className="chat-dot" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#bbb', animationDelay: '0s' }} />
                                    <Box component="span" className="chat-dot" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#bbb', animationDelay: '0.15s' }} />
                                    <Box component="span" className="chat-dot" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#bbb', animationDelay: '0.3s' }} />
                                </Box>
                            )}
                            <div ref={messagesEndRef} />
                        </Box>

                        {/* mic permission denied — inline Alert above the input row, dismissible via the X */}

                        {micDenied && (
                            <Box sx={{ px: 1, pt: 1 }}>
                                <Alert
                                    variant="filled"
                                    severity="error"
                                    onClose={() => setMicDenied(false)}
                                    sx={{ alignItems: 'center', userSelect: 'none', fontSize: 13 }}
                                >
                                    {t('chatbot.mic_denied')}
                                </Alert>
                            </Box>
                        )}

                        {/* input row — a single hybrid action button on the right morphs by priority: cancel (loading) > stop (listening) > send (has text) > mic (default) */}

                        <Box sx={{
                            p: 1, borderTop: '1px solid #e0e0e0', bgcolor: 'white',
                        }}>
                            <TextField
                                size="small"
                                fullWidth
                                value={input}
                                onChange={e => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                onFocus={onInputFocus}
                                placeholder={isListening ? t('chatbot.listening') : t('chatbot.placeholder')}
                                disabled={loading}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {loading ? (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => ChatManager.cancelCurrentTurn()}
                                                    title={t('chatbot.cancel')}
                                                    sx={{ color: '#c62828' }}
                                                >
                                                    <Cancel fontSize="small" />
                                                </IconButton>
                                            ) : isListening ? (
                                                <IconButton
                                                    size="small"
                                                    onClick={toggleListening}
                                                    title={t('chatbot.speech_stop')}
                                                    sx={{ color: '#c62828', animation: 'micPulse 1.5s ease-in-out infinite' }}
                                                >
                                                    <Stop fontSize="small" />
                                                </IconButton>
                                            ) : input.trim() ? (
                                                <IconButton
                                                    size="small"
                                                    onClick={handleSend}
                                                    title={t('chatbot.send')}
                                                >
                                                    <ArrowUpward fontSize="small" />
                                                </IconButton>
                                            ) : speechSupported ? (
                                                <IconButton
                                                    size="small"
                                                    onClick={toggleListening}
                                                    title={t('chatbot.speech')}
                                                >
                                                    <Mic fontSize="small" />
                                                </IconButton>
                                            ) : (
                                                // no speech support and no text — show a disabled send affordance so the layout doesn't jump
                                                <IconButton
                                                    size="small"
                                                    title={t('chatbot.send')}
                                                    disabled
                                                >
                                                    <ArrowUpward fontSize="small" />
                                                </IconButton>
                                            )}
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13, pr: 0.5 } }}
                            />
                        </Box>
                    </Paper>
                </Box>
            )}
        </>
    )
}
